import {TradefloorApiService} from "../../../../js/services/tradefloorApi.service";
import {OrderFormatService} from "../../../../orders/services/format/OrderFormatService";
import {SimpleModalService} from "../../../../types/SimpleModalService";
import {IHttpPromise, translate} from "angular";
import {PlacedOrdersStore} from "../../../../orders/store/placed/PlacedOrdersStore";
import {AccountStore} from "../../../../account/store/AccountStore";
import {MultiAccountOrderRequest} from "./models/PlaceMultiAccountOrderRequest";
import {PortfolioType} from "../../../models/Portfolio";
import {OrderNumber} from "../../../../orders/models/OrderRecord";
import {VettingLevel, VettingOutcomes} from "../types/OrderVettingResponse";
import {getOrderApiErrorDetails} from "../../../../js/error.utils";

export class MultiAccountTradeService {

    public static readonly id = "multiAccountTradeService";

    /** @ngInject */
    public constructor(private readonly tradefloorApi: TradefloorApiService,
                       private readonly orderFormatService: OrderFormatService,
                       private readonly simpleModalService: SimpleModalService,
                       private readonly $translate: translate.ITranslateService,
                       private readonly growl: Growl,
                       private readonly placedOrdersStore: PlacedOrdersStore,
                       private readonly accountStore: AccountStore) {
    }

    public async placeMultiAccountTrades(requestData: MultiAccountOrderRequest) {
        const results = await this.sequenceRun(requestData?.orders ?? [], (o) => {
            return this.tradefloorApi.placeSingleOrder(o.portfolioId,
                                                       o.placeOrderRequest,
                                                       o.placeOrderRequest.description,
                                                       false);
        });
        this.processOrderResults(results);

    }

    public processOrderResults(results: Array<any>) {
        const failResults = results.filter( r => r.result.status !== 200);
        const successResults = results.filter(r => r.result.status === 200);
        if (failResults.length > 0) {
            failResults.forEach( async (entry) => {
                const order = entry.request;
                const orderDescription = await this.orderFormatService.format(order);
                if (entry.result.status === 412) {
                    const orderVettingResult = entry.result.data;
                    this.processVettingError(orderDescription, orderVettingResult, order);
                }
            });
        }

        const orderErrorDescriptions = results.filter((entry) => entry.result.status !== 200)
                                              .filter((entry) => entry.result.status !== 412)
                                              .map((entry) => getOrderApiErrorDetails(entry.result));

        if (orderErrorDescriptions.length > 0) {
            const pl = orderErrorDescriptions.length > 1;
            this.simpleModalService.error(null,
                                          `<pre>${orderErrorDescriptions.join("\r\n")}</pre>`,
                                          "Order Error", "lg");
        }

        if (successResults.length > 0) {
            successResults.forEach( async entry => {
                const orderIdentifier = entry.result.data;
                this.processPlacedOrder(entry.request.portfolioId, orderIdentifier);
            });

            this.alertSuccess(successResults);
        }

        return this.findAllocatedOrders(results);
    }

    private async alertSuccess(results: any[]) {
        const messages: string[] = [];
        const accountPromises = results.map((result) =>
                    this.accountStore.accounts.take(1).toPromise()
                        .then((accounts) => {
                            const account = accounts.find((a) => a.id === result.request.portfolioId);
                            return `Order made by ${account?.number} has been successfully created with ${result.result.data[0]} order number`;
                        })
        );

        const resolvedMessages = await Promise.all(accountPromises);
        messages.push(...resolvedMessages);

        this.simpleModalService.error(null,
                                      `<pre>${messages.join("\r\n")}</pre>`,
                                      "Successful orders", "lg");
    }

    private findAllocatedOrders(results: Array<any>): Array<any> {
        return results.filter((result) => result.result.status === 200)
                      .map((result) => result.request)
                      .map((request) => {
                          return {
                              code: request.code,
                              quantity: request.quantity
                          };
                      });
    }

    private async processVettingError(orderDescription: string, vettingResult: VettingOutcomes, order: any) {
        const defaultConfig: GrowlConfig = {
            title: "Errors when placing Trade",
            ttl: -1
        };

        const firstLine = `Unable to place ${orderDescription}`;

        const errorMessages = vettingResult.outcomes.filter((o) => o.level === VettingLevel.ERROR)
                                           .map((outcome) => outcome.message)
                                           .map((message) => this.$translate(message.code, message.parameters));

        const errors = await Promise.all(errorMessages);

        if (errors.length === 0) {
            throw Error("An unexpected error occurred while attempting to place trade");
        } else if (errors.length === 1) {
            const error = errors[0];
            const errorDescription = `<div>${firstLine}</div>
                                      <div>${error}</div>`;
            this.growl.error(errorDescription, defaultConfig);
        } else {
            const errorDescriptions = errors.map((d) => `<li>${d}</li>`).join();
            const message = `<div>${firstLine}</div>
                             <ul>
                                ${errorDescriptions}
                             </ul>`;
            this.growl.error(message, defaultConfig);
        }
    }

    private processPlacedOrder(portfolioId: number, result: number) {
        this.accountStore
            .accounts
            .take(1)
            .subscribe((accounts) => {
                const account = accounts.find((a) => a.id === portfolioId);
                if (account == null) {
                    return;
                } else if (account.portfolioType === PortfolioType.BROKER) {
                    this.placedOrdersStore.brokerOrderPlaced(result as OrderNumber);
                } else if (account.portfolioType === PortfolioType.SIMULATED) {
                    this.placedOrdersStore.simulatedOrderPlaced(result);
                }
            });
    }

    public sequenceRun<T>(array: ReadonlyArray<T>, fn: (data: T) => IHttpPromise<any>) {
        const promises = array.map((item) => fn(item));
        const mapped = promises.map((p, index) => this.toResult(p, array[index]));
        return Promise.all(mapped);
    }

    private async toResult<T>(p: IHttpPromise<any>, item: T): Promise<any> {
        try {
            const resp = await p;
            return {success: true, request: item, result: resp};
        } catch (error) {
            return {success: false, request: item, result: error};
        }
    }

}
